import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CustomTranslationsService, STATE } from 'src/app/operational-data-common/translations';
import { SUFFIX } from 'src/app/shared/common/units';

import {
  BurnerStatisticsProperties,
  Feature,
  FeatureNames,
  HeatingCompressorsProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  OffPeakSignal,
  OnOff,
  frostProtectionProperties,
  pvSignal,
} from '@vi-iot/client';

import { DeviceTypes, DevicesStore, InstallationFeaturesStore } from '@store';

import { map, takeUntil } from 'rxjs/operators';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { TranslateService } from '@ngx-translate/core';
import { CONDITIONAL_OFF_PEAK_SIGNAL } from 'src/app/features/timemanager/schedule.enums';

@Component({
  selector: 'app-refrigerant-circuit-features-panel',
  templateUrl: './refrigerant-circuit-features-panel.component.html',
  styleUrls: ['./refrigerant-circuit-features-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefrigerantCircuitFeaturesPanelComponent implements OnInit, OnDestroy {
  readonly suffix = SUFFIX;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  STATE = STATE;
  SUFFIX = SUFFIX;
  OnOff = OnOff;
  readonly heatingDevice = DeviceTypes.Heating;
  readonly CONDITIONAL_OFF_PEAK_SIGNAL = CONDITIONAL_OFF_PEAK_SIGNAL;
  readonly offPeakSignal = OffPeakSignal;
  @Input() refrigerantCircuitId: string;
  @Input() header: string;
  @Input() deviceSelectId: string;
  @Input() deviceId: string;

  refrigerantCircuitStatus$: Observable<Feature<HeatingCompressorsProperties, never>>;
  refrigerantCircuitStatistics$: Observable<Feature<BurnerStatisticsProperties, never>>;
  spaceFrostProtection$: Observable<Feature<IsActiveProperty, never>>;
  frostProtection$: Observable<Feature<frostProtectionProperties, never>>;
  heatingNoiseReductionMode$: Observable<string>;
  isE3HeatPumpDevice$: Observable<boolean>;

  dhwAndSpaceFrostProtection: { status: string; value: string };
  pvAvailable: pvSignal;

  compressorRPM$: Observable<Feature<IsNumberValueProperty, never>>;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    protected customTranslationsService: CustomTranslationsService,
    private devicesStore: DevicesStore,
    private translate: TranslateService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.pvAvailable = pvSignal.pvAvailable;
    this.frostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.frostProtection,
      this.deviceId
    );
    this.spaceFrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.spaceFrostProtection,
      this.deviceId
    );
    this.devicesStore.selectedDevice$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((device) => {
      this.isE3HeatPumpDevice$ = this.devicesStore.isE3HeatpumpDevice(device);
    });
    this.heatingNoiseReductionMode$ = this.installationFeaturesStore.getHeatingNoiseReductionMode(
      this.deviceId
    );
    this.refrigerantCircuitStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.singleCompressorStatus(this.refrigerantCircuitId),
      this.deviceId
    );
    this.refrigerantCircuitStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.singleCompressorStatistics(this.refrigerantCircuitId),
      this.deviceId
    );
    this.compressorRPM$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, never>(
        FeatureNames.compressorSpeedCurrent,
        this.deviceId
      )
      .pipe(
        map((feature) => ({
          ...feature,
          properties: {
            ...feature?.properties,
            value: {
              ...feature?.properties?.value,
              value: feature?.properties?.value?.value * 60 || null,
            },
          },
        }))
      );

    this.getFrostStatus();
  }

  getFrostStatus(): void {
    let spaceFrostProtectionValue: boolean;
    let frostProtectionValue: string;
    this.spaceFrostProtection$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((space) => {
      spaceFrostProtectionValue = space?.properties?.active?.value;
    });
    this.frostProtection$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((space) => {
      frostProtectionValue = space?.properties?.status?.value;
    });
    if (spaceFrostProtectionValue && frostProtectionValue === OnOff.on) {
      this.dhwAndSpaceFrostProtection = {
        status: this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.REFRIGERANT_CIRCUIT.STATUS.SPACE_AND_DHW_FROST_PROTECTION'
        ),
        value: this.translate.instant(this.customTranslationsService.STATE.on),
      };
    } else if (spaceFrostProtectionValue && frostProtectionValue !== OnOff.on) {
      this.dhwAndSpaceFrostProtection = {
        status: this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.REFRIGERANT_CIRCUIT.STATUS.SPACE_FROST_PROTECTION'
        ),
        value: this.translate.instant(this.customTranslationsService.STATE.on),
      };
    } else if (!spaceFrostProtectionValue && frostProtectionValue === OnOff.on) {
      this.dhwAndSpaceFrostProtection = {
        status: this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.REFRIGERANT_CIRCUIT.STATUS.DHW_FROST_PROTECTION'
        ),
        value: this.translate.instant(this.customTranslationsService.STATE.on),
      };
    } else {
      this.dhwAndSpaceFrostProtection = {
        status: this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.REFRIGERANT_CIRCUIT.STATUS.SPACE_AND_DHW_FROST_PROTECTION'
        ),
        value: this.translate.instant(this.customTranslationsService.STATE.off),
      };
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
